import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import Link from 'next/link'
import { ArrowRight, Building2, Check, Store } from 'lucide-react'
import { cn } from '@/lib/utils'
import { LogoMark } from '@/components/brand/logo'
import { buttonVariants } from '@/components/ui/button'

interface Service {
    icon: LucideIcon
    label: string
    title: string
    description: string
    points: string[]
}

const SERVICES: Service[] = [
    {
        icon: Store,
        label: 'Local businesses',
        title: 'A website that brings customers through the door.',
        description: 'Cafés, salons, trades, shops and studios. We give your business a sharp, professional home online, so people nearby can find you, trust you and get in touch.',
        points: [
            'Looks great on every phone',
            'Click-to-call, maps and opening hours',
            'Your services and prices, front and centre'
        ]
    },
    {
        icon: Building2,
        label: 'Big companies',
        title: 'A website that keeps up with your company.',
        description: 'Head offices, national brands and growing teams. We design and build polished, multi-page websites that match your brand and handle real traffic.',
        points: [
            'Multi-page sites and landing pages',
            'Built around your brand guidelines',
            'Room to grow as your business does'
        ]
    }
]

const ServicesSection: FC = (): ReactNode => {
    return (
        <section id='services' aria-labelledby='services-title' className='scroll-mt-16 bg-paper py-20 text-ink sm:py-28'>
            <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
                <div className='max-w-3xl'>
                    <p className='text-sm font-semibold uppercase tracking-[0.18em] text-ink/55'>
                        What we do
                    </p>
                    <h2
                        id='services-title'
                        className='mt-4 text-balance font-heading text-3xl font-bold leading-tight tracking-[-0.02em] sm:text-5xl'
                    >
                        Websites for every size of business.
                    </h2>
                    <p className='mt-5 max-w-2xl text-lg leading-relaxed text-muted-foreground'>
                        Whether you run one shop or a hundred offices, you get the same thing from us: a fast, good-looking website that makes it easy for customers to choose you.
                    </p>
                </div>

                <div className='mt-14 grid gap-6 lg:grid-cols-2'>
                    {SERVICES.map(({ icon: Icon, label, title, description, points }) => (
                        <article
                            key={label}
                            className='flex flex-col rounded-[2rem] border border-border bg-card p-8 transition-shadow duration-300 hover:shadow-xl hover:shadow-ink/5 sm:p-10'
                        >
                            <div className='flex items-center justify-between gap-4'>
                                <span className='flex size-14 items-center justify-center rounded-2xl bg-volt text-ink'>
                                    <Icon className='size-6' aria-hidden='true' />
                                </span>
                                <span className='text-xs font-semibold uppercase tracking-[0.16em] text-ink/50'>
                                    {label}
                                </span>
                            </div>

                            <h3 className='mt-8 font-heading text-2xl font-bold leading-snug tracking-[-0.01em] sm:text-3xl'>
                                {title}
                            </h3>
                            <p className='mt-4 leading-relaxed text-muted-foreground'>
                                {description}
                            </p>

                            <ul className='mt-8 space-y-3 border-t border-border pt-8'>
                                {points.map(point => (
                                    <li key={point} className='flex items-start gap-3 text-[15px] font-medium'>
                                        <span className='mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-ink text-volt'>
                                            <Check className='size-3' strokeWidth={3} aria-hidden='true' />
                                        </span>
                                        {point}
                                    </li>
                                ))}
                            </ul>
                        </article>
                    ))}
                </div>

                <div className='relative mt-6 overflow-hidden rounded-[2rem] bg-ink text-paper'>
                    <div
                        aria-hidden='true'
                        className='pointer-events-none absolute -right-24 -top-24 size-96 rounded-full bg-volt/15 blur-3xl'
                    />

                    <div className='relative grid gap-10 p-8 sm:p-12 lg:grid-cols-[1.6fr_1fr] lg:items-center'>
                        <div>
                            <p className='text-sm font-semibold uppercase tracking-[0.18em] text-volt'>
                                Affordable by design
                            </p>
                            <h3 className='mt-4 text-balance font-heading text-3xl font-bold leading-tight tracking-[-0.02em] sm:text-4xl'>
                                Agency quality, without the agency price tag.
                            </h3>
                            <p className='mt-5 max-w-xl text-lg leading-relaxed text-paper/70'>
                                Tell us what you need and roughly what you'd like to spend, and we'll come back with a clear quote for a website that fits.
                            </p>
                            <Link
                                href='#contact'
                                className={cn(buttonVariants({ size: 'xl' }), 'mt-8 focus-visible:ring-volt/60')}
                            >
                                Get a quote
                                <ArrowRight className='size-4' aria-hidden='true' />
                            </Link>
                        </div>

                        <div aria-hidden='true' className='hidden justify-center lg:flex'>
                            <LogoMark className='size-44 rotate-6 drop-shadow-[0_30px_40px_rgba(0,0,0,0.45)]' />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default ServicesSection
